import { observable, action, toJS, computed, runInAction } from 'mobx';
import { createTransformer } from 'mobx-utils';
import { format } from 'date-fns';
import DealMapperViewStore from './dealMapperViewStore';
import { getId } from './dealMapUtil/common';
import authStore from '../authStore';
import orgStore from '../orgStore';
import {
  VoucherTransactionSchema,
  VoucherSchema,
  DealSchema,
  DealTransactionSchema,
} from '../../validation';

const filterUnchangedObjects = i => i.Pending || i.PendingDelete;

const isIdTemp = id => id.toString().substring(0, 4) === 'tmp-';

const getDealTransactionTypeFromVoucher = voucher => {
  if (
    voucher.VoucherTypeId === 2 ||
    voucher.VoucherTypeId === 4 ||
    voucher.VoucherTypeId === 6
  ) {
    return 'AP';
  }

  if (
    voucher.VoucherTypeId === 1 ||
    voucher.VoucherTypeId === 3 ||
    voucher.VoucherTypeId === 5
  ) {
    return 'AR';
  }

  throw new Error('I DUNNO');
};

const areVoucherandDTCompatible = (dt, voucher) => {
  const { TranType } = dt;
  const { VoucherTypeCode: vTypeCode } = voucher;
  if (TranType === 'AP') {
    const vtOptions = ['RCR', 'REC', 'RDP'];
    return !vtOptions.includes(vTypeCode);
  }
  if (TranType === 'AR') {
    const vtOptions = ['PCR', 'PAY', 'PDP'];
    return !vtOptions.includes(vTypeCode);
  }
  return false;
};

const getDealTransactionAccounts = (voucher, voucherTransaction) => {
  const dealTransactionType = getDealTransactionTypeFromVoucher(voucher);
  let CreditAccount;
  let DebitAccount;

  if (dealTransactionType === 'AP') {
    CreditAccount = voucher.AccountId;
    DebitAccount = voucherTransaction.AccountId;
  } else if (dealTransactionType === 'AR') {
    CreditAccount = voucherTransaction.AccountId;
    DebitAccount = voucher.AccountId;
  } else {
    throw new Error(`NO SUPPORT FOR TYPE ${dealTransactionType}`);
  }

  return {
    CreditAccount,
    DebitAccount,
  };
};

const getVoucherTypeFromDealTransaction = (
  dealTransaction,
  isInvoiceDefault,
) => {
  if (isInvoiceDefault) {
    if (dealTransaction.TranType === 'AP') {
      // is positive return PAYABLE (2)
      // is negative return PAYABLE CREDIT (4)
      return dealTransaction.Price >= 0 ? 2 : 4;
    }

    // is positive return RECEIVABLE (1)
    // is negative return RECEIVABLE CREDIT (3)
    if (dealTransaction.TranType === 'AR') {
      return dealTransaction.Price >= 0 ? 1 : 3;
    }

    // FIXME: what do I return here? For now I'll return a negative so it never matches
    return -1;
  }
  if (dealTransaction.TranType === 'AP') {
    return 6;
  }

  return 5;
};

const getVoucherTransactionAccountFromDealTransaction = dealTransaction =>
  dealTransaction.TranType === 'AP'
    ? dealTransaction.DebitAccount
    : dealTransaction.CreditAccount;

const getVoucherAccountFromDealTransaction = dealTransaction =>
  dealTransaction.TranType === 'AP'
    ? dealTransaction.CreditAccount
    : dealTransaction.DebitAccount;

const formatForPersist = dealMap => {
  const filteredDeals = dealMap.deals.filter(filterUnchangedObjects);
  const filteredDealTransactions = dealMap.dealTransactions.filter(
    filterUnchangedObjects,
  );
  const filtereddtvts = dealMap.dtvts.filter(filterUnchangedObjects);
  const filteredVoucherTransactions = dealMap.voucherTransactions.filter(
    filterUnchangedObjects,
  );
  const filteredVouchers = dealMap.vouchers.filter(filterUnchangedObjects);
  // clone objects to prevent mutation
  const formattedDealMap = JSON.parse(
    JSON.stringify({
      deals: filteredDeals,
      dealTransactions: filteredDealTransactions,
      dtvts: filtereddtvts,
      voucherTransactions: filteredVoucherTransactions,
      vouchers: filteredVouchers,
    }),
  );
  const removeList = [
    'ApprovedByName',
    'StatusName',
    'ModifiedTimestamp',
    'ModifiedBy',
    'CreatedTimestamp',
    'CreatedBy',
    'IsEditable',
  ];
  Object.keys(formattedDealMap).forEach(key => {
    formattedDealMap[key].forEach(arrayObj => {
      const curArrayObj = arrayObj;
      Object.keys(arrayObj).forEach(arrayObjKey => {
        // Remove all null keys
        if (curArrayObj[arrayObjKey] == null) {
          delete curArrayObj[arrayObjKey];
        }
        // TODO: UPDATE THIS
        // Remove fields that Bosman has defined and "Pending" key
        else if (removeList.includes(arrayObjKey))
          delete curArrayObj[arrayObjKey];
        // Anything with tmp- --> prefix with Pending
        else if (
          typeof curArrayObj[arrayObjKey] === 'string' &&
          curArrayObj[arrayObjKey].includes('tmp-')
        ) {
          curArrayObj[`Pending${arrayObjKey}`] = curArrayObj[arrayObjKey];
          delete curArrayObj[arrayObjKey];
        }
      });
    });
  });

  return formattedDealMap;
};

const addPersistedToArray = array =>
  array.map(i => ({ ...i, Persisted: true }));

const addPersistedToDealMap = (dealmap, SeedObject) => {
  const modDealMap = {
    deals: addPersistedToArray(dealmap.deals),
    dealTransactions: addPersistedToArray(dealmap.dealTransactions),
    dtvts: addPersistedToArray(dealmap.dtvts),
    voucherTransactions: addPersistedToArray(dealmap.voucherTransactions),
    vouchers: addPersistedToArray(dealmap.vouchers),
  };

  if (SeedObject === 'deal') {
    modDealMap.deals = modDealMap.deals.map(d => ({ ...d, Seed: true }));
  } else if (SeedObject === 'voucher') {
    modDealMap.vouchers = modDealMap.vouchers.map(i => ({ ...i, Seed: true }));
  }

  return modDealMap;
};

const zipArrays = (arrayToAdd, baseArray, uniqueValue) => {
  const newArray = [...arrayToAdd, ...baseArray];
  const finalArray = [
    ...arrayToAdd.filter(item => item.Seed),
    ...baseArray.filter(item => item.Seed),
  ];
  newArray.forEach(i => {
    if (finalArray.find(a => a[uniqueValue] === i[uniqueValue])) {
      return;
    }

    finalArray.push(i);
  });

  const filterDuplicates = finalArray.filter(
    (v, i, a) => a.findIndex(t => t[uniqueValue] === v[uniqueValue]) === i,
  );

  return filterDuplicates;
};

const zipArraysMultiKey = (array1, array2, unqiueValue1, uniqueValue2) => {
  const newArray = [...array1, ...array2];
  const finalArray = [];
  newArray.forEach(i => {
    if (
      finalArray.find(
        a =>
          a[unqiueValue1] === i[unqiueValue1] &&
          a[uniqueValue2] === i[uniqueValue2],
      )
    ) {
      return;
    }

    finalArray.push(i);
  });

  return finalArray;
};

// TODO: Formatting returns a string. Math operations with a string and a number are very bad.
// Consider casting the result to a float?
const multiplyTwo = (a, b) => {
  // const result = formatPrice(a * b);
  // console.log('multiply', a, b, result);
  // return result;
  return a * b;
};

const divideTwo = (a, b) => {
  // const result = formatPrice(a / b);
  // console.log('divide', a, b);
  // return result;
  return a / b;
};

// Takes an array of values and returns the first one that isn't null or zero length
// If no value is found it returns 'N/A'
const assignByPriority = valueArray => {
  const val = valueArray.find(curVal => curVal != null && curVal.length > 0);
  return val || 'N/A';
};

export default class DealMapperStore {
  @observable deals = [];
  @observable dealTransactions = [];
  @observable dtvts = [];
  @observable voucherTransactions = [];
  @observable vouchers = [];
  @observable loading = false;
  @observable selectedVoucherTransaction = null;
  @observable selectedDealTransaction = null;

  constructor() {
    this.dealMapperViewStore = new DealMapperViewStore(this);
  }

  @action
  reset() {
    this.deals = [];
    this.dealTransactions = [];
    this.dtvts = [];
    this.voucherTransactions = [];
    this.vouchers = [];
    this.loading = false;
    this.selectedVoucherTransaction = null;
    this.selectedDealTransaction = null;
    this.dealMapperViewStore = new DealMapperViewStore(this);
  }

  getVoucherTransactionsOnVoucher = voucher =>
    this.voucherTransactions.filter(it => it.VoucherId === voucher.VoucherId);

  getDtvtsOnVoucherTransaction = voucherTransaction =>
    this.dtvts.filter(
      dtvt => dtvt.VoucherTranId === voucherTransaction.VoucherTranId,
    );

  getDealTransactionOnDtvt = dtvt =>
    this.dealTransactions.find(dt => dt.DealTranId === dtvt.DealTranId);

  getDealOnDealTransaction = dealTransaction =>
    this.deals.find(d => dealTransaction.DealId === d.DealId);

  getDealTransactionsOnDeal = deal =>
    this.dealTransactions.filter(dt => dt.DealId === deal.DealId);

  getDtvtsOnDealTransaction = dealTransaction =>
    this.dtvts.filter(dtvt => dtvt.DealTranId === dealTransaction.DealTranId);

  getVoucherTransactionOnDtvt = dtvt =>
    this.voucherTransactions.find(
      it => it.VoucherTranId === dtvt.VoucherTranId,
    );

  // TODO: use reduce
  getAllDtvtsOnVoucherTransactions = vts => {
    const dtvts = [];
    vts.forEach(vt => {
      dtvts.push(...this.getDtvtsOnVoucherTransaction(vt));
    });

    return dtvts;
  };

  getVoucherOnVoucherTransaction = voucherTransaction =>
    this.vouchers.find(i => i.VoucherId === voucherTransaction.VoucherId);

  getDtvtsOnDeal = deal => {
    const dtvts = [];
    const dealTransactions = this.getDealTransactionsOnDeal(deal);
    dealTransactions.forEach(dt => {
      dtvts.push(...this.getDtvtsOnDealTransaction(dt));
    });

    return dtvts;
  };

  getDtvtsOnVoucher = voucher => {
    const dtvts = [];
    const voucherTransactions = this.getVoucherTransactionsOnVoucher(voucher);
    voucherTransactions.forEach(it => {
      dtvts.push(...this.getDtvtsOnVoucherTransaction(it));
    });

    return dtvts;
  };

  getDealsOnVoucher = voucher => {
    const relatedDeals = [];
    // get vts on voucher
    const vtsOnVoucher = this.getVoucherTransactionsOnVoucher(voucher);
    vtsOnVoucher.forEach(it => {
      // get dtvts on it
      const dtvtsOnIt = this.getDtvtsOnVoucherTransaction(it);
      dtvtsOnIt.forEach(dtvt => {
        try {
          // get dt on dtvt
          const dtOndtvt = this.getDealTransactionOnDtvt(dtvt);
          // get deal on dt
          relatedDeals.push(this.getDealOnDealTransaction(dtOndtvt));
        } catch {
          // supress error
        }
      });
    });
    // console.log(toJS(relatedDeals[0]));
    return relatedDeals;
  };

  getVouchersOnDeal = deal => {
    const relatedVouchers = [];
    // get dts on deal
    const dtsOnDeal = this.getDealTransactionsOnDeal(deal);
    dtsOnDeal.forEach(dt => {
      // get dtvts on dt
      const dtvtsOnDt = this.getDtvtsOnDealTransaction(dt);
      dtvtsOnDt.forEach(dtvt => {
        try {
          // get vt on dtvt
          const vtOndtvt = this.getVoucherTransactionOnDtvt(dtvt);
          // get voucher on it
          const voucherOnIt = this.getVoucherOnVoucherTransaction(vtOndtvt);

          relatedVouchers.push(voucherOnIt);
        } catch {
          // TODO: if there are no related vouchers suppress error
        }
      });
    });

    return relatedVouchers;
  };

  setDealMap = ({
    deals,
    dealTransactions,
    dtvts,
    voucherTransactions,
    vouchers,
  }) => {
    this.loading = true;
    this.deals = zipArrays(deals, this.deals, 'DealId');
    this.dealTransactions = zipArrays(
      dealTransactions,
      this.dealTransactions,
      'DealTranId',
    );
    this.dtvts = zipArraysMultiKey(
      dtvts,
      this.dtvts,
      'DealTranId',
      'VoucherTranId',
    );
    this.voucherTransactions = zipArrays(
      voucherTransactions,
      this.voucherTransactions,
      'VoucherTranId',
    );
    this.vouchers = zipArrays(vouchers, this.vouchers, 'VoucherId');
    this.loading = false;
  };

  @computed get isAnythingPending() {
    return (
      !this.deals.every(d => !d.Pending) ||
      !this.dealTransactions.every(dt => !dt.Pending) ||
      !this.dtvts.every(dtvt => !dtvt.Pending) ||
      !this.voucherTransactions.every(it => !it.Pending) ||
      !this.vouchers.every(i => !i.Pending)
    );
  }

  @action async validateObjects() {
    let isValid = true;
    const validationErrors = [];

    await Promise.all(
      this.voucherTransactions.map(async i => {
        try {
          await VoucherTransactionSchema.validate(i, {
            abortEarly: false,
          });
        } catch (e) {
          const errorList = e.errors.map(
            err => `${err} on voucher transaction`,
          );
          validationErrors.push(...errorList);
          isValid = false;
        }
      }),
      this.vouchers.map(async i => {
        try {
          await VoucherSchema.validate(i, {
            abortEarly: false,
          });
        } catch (e) {
          const errorList = e.errors.map(err => `${err} on voucher`);
          validationErrors.push(...errorList);
          isValid = false;
        }
      }),
      this.dealTransactions.map(async i => {
        try {
          await DealTransactionSchema.validate(i, {
            abortEarly: false,
          });
        } catch (e) {
          const errorList = e.errors.map(err => `${err} on deal transaction`);
          validationErrors.push(...errorList);
          isValid = false;
        }
      }),
      this.deals.map(async i => {
        try {
          await DealSchema.validate(i, {
            abortEarly: false,
          });
        } catch (e) {
          const errorList = e.errors.map(err => `${err} on deal`);
          validationErrors.push(...errorList);
          isValid = false;
        }
      }),
    );

    const uniqueValErrors = [...new Set(validationErrors)];
    return { isValid, validationErrors: uniqueValErrors };
  }

  @action createDtvt({ DealTranId, VoucherTranId, Amount }) {
    const newDtvt = {
      DealTranId,
      VoucherTranId,
      Amount,
    };
    // if the dtvt already exists don't do anything
    if (
      this.dtvts.find(
        dtvt2 =>
          dtvt2.DealTranId === newDtvt.DealTranId &&
          dtvt2.VoucherTranId === newDtvt.VoucherTranId,
      )
    )
      return;

    this.dtvts.push({
      ...newDtvt,
      Pending: true,
    });
  }

  @action setSelectedDealTransaction(dealTransaction) {
    this.selectedDealTransaction = dealTransaction;

    if (this.selectedVoucherTransaction) {
      try {
        const voucherForVT = this.getVoucherOnVoucherTransaction(
          this.selectedVoucherTransaction,
        );
        const areCompatible = areVoucherandDTCompatible(
          dealTransaction,
          voucherForVT,
        );
        if (areCompatible) {
          this.createDtvt({
            DealTranId: this.selectedDealTransaction.DealTranId,
            VoucherTranId: this.selectedVoucherTransaction.VoucherTranId,
            Amount: multiplyTwo(
              this.selectedDealTransaction.Price,
              this.selectedDealTransaction.Quantity,
            ),
          });
          this.removeSelectedDealTransaction();
          this.removeSelectedVoucherTransaction();
        } else {
          this.removeSelectedDealTransaction();
          return 'INCOMPATIBLE';
        }
      } catch (e) {
        console.log(error);
      }
    }
  }

  @action removeSelectedDealTransaction() {
    this.selectedDealTransaction = null;
  }

  @action setSelectedVoucherTransaction(voucherTransaction) {
    this.selectedVoucherTransaction = voucherTransaction;
    if (this.selectedDealTransaction) {
      try {
        console.log('DTVT 1');
        const voucherForVT = this.getVoucherOnVoucherTransaction(
          voucherTransaction,
        );

        const areCompatible = areVoucherandDTCompatible(
          this.selectedDealTransaction,
          voucherForVT,
        );

        if (areCompatible) {
          this.createDtvt({
            DealTranId: this.selectedDealTransaction.DealTranId,
            VoucherTranId: this.selectedVoucherTransaction.VoucherTranId,
            Amount: multiplyTwo(
              this.selectedVoucherTransaction.Price,
              this.selectedVoucherTransaction.Quantity,
            ),
          });
          this.removeSelectedDealTransaction();
          this.removeSelectedVoucherTransaction();
        } else {
          this.removeSelectedVoucherTransaction();
          return 'INCOMPATIBLE';
        }
      } catch (e) {
        console.log(e);
      }
    }
  }

  @action removeSelectedVoucherTransaction() {
    this.selectedVoucherTransaction = null;
  }

  @computed get deal() {
    return createTransformer(id => this.deals.find(d => d.DealId === id));
  }

  @computed get dealTransaction() {
    return createTransformer(id =>
      this.dealTransactions.find(dt => dt.DealTranId === id),
    );
  }

  @computed get dtvt() {
    return createTransformer(({ dealTransactionId, voucherTransactionId }) =>
      this.dtvts.find(
        dtvt =>
          dtvt.DealTranId === dealTransactionId &&
          dtvt.VoucherTranId === voucherTransactionId,
      ),
    );
  }

  @computed get voucherTransaction() {
    return createTransformer(id =>
      this.voucherTransactions.find(it => it.VoucherTranId === id),
    );
  }

  @computed get voucher() {
    return createTransformer(id => this.vouchers.find(i => i.VoucherId === id));
  }

  @computed get dealMap() {
    return {
      deals: this.deals,
      dealTransactions: this.dealTransactions,
      dtvts: this.dtvts,
      voucherTransactions: this.voucherTransactions,
      vouchers: this.vouchers,
    };
  }

  @action updateVoucherList(newList) {
    this.vouchers = newList;
  }

  @action loadVouchers = async voucherIds => {
    const response = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/deal-map-voucher/${voucherIds.join(
        ',',
      )}`,
    );

    this.setDealMap(addPersistedToDealMap(response.data, 'voucher'));
  };

  @action deleteDtvt(dtvt) {
    this.dtvts = this.dtvts
      .map(dtvt2 => {
        if (
          dtvt.DealTranId === dtvt2.DealTranId &&
          dtvt.VoucherTranId === dtvt2.VoucherTranId
        ) {
          if (!dtvt2.Persisted) {
            return null;
          }

          return {
            ...dtvt2,
            PendingDelete: true,
          };
        }
        return dtvt2;
      })
      // filter our nulls
      .filter(dtvt2 => dtvt2);
  }

  @action deleteDealTransaction(dealTransaction) {
    this.dealTransactions = this.dealTransactions
      .map(dt => {
        if (dt.DealTranId === dealTransaction.DealTranId) {
          if (!dt.Persisted) {
            return null;
          }

          return {
            ...dt,
            PendingDelete: true,
          };
        }

        return dt;
      })
      // get rid of nulls
      .filter(dt => dt);

    this.dtvts = this.dtvts
      .map(dtvt => {
        if (dtvt.DealTranId === dealTransaction.DealTranId) {
          if (!dtvt.Persisted) {
            return null;
          }

          return {
            ...dtvt,
            PendingDelete: true,
          };
        }

        return dtvt;
      })
      .filter(i => i);
  }

  @action deleteVoucherTransation(voucherTransaction) {
    this.voucherTransactions = this.voucherTransactions
      .map(it => {
        if (it.VoucherTranId === voucherTransaction.VoucherTranId) {
          if (!it.Persisted) {
            return null;
          }
          return {
            ...it,
            PendingDelete: true,
          };
        }

        return it;
      })
      .filter(i => i);

    this.dtvts = this.dtvts
      .map(dtvt => {
        if (dtvt.VoucherTranId === voucherTransaction.VoucherTranId) {
          if (!dtvt.Persisted) {
            return null;
          }

          return {
            ...dtvt,
            PendingDelete: true,
          };
        }

        return dtvt;
      })
      .filter(dtvt => dtvt);
  }

  @action addDeal({
    Title,
    Description,
    ContractDate,
    TransactionDate,
    BillableDate,
    PayableDate,
    ApprovedBy,
    StatusId,
    DealNumber,
  }) {
    const dealToPush = {
      DealId: getId(),
      Title,
      Description,
      ContractDate,
      TransactionDate,
      BillableDate,
      PayableDate,
      ApprovedBy,
      StatusId,
      DealNumber,
      Pending: true,
    };
    this.deals.push(dealToPush);
    return dealToPush;
  }

  @action addDealTransaction({
    DealId,
    Type,
    CategoryId,
    ContactId,
    Price,
    Quantity,
    Description,
    DebitAccount,
    CreditAccount,
  }) {
    // TODO: validate holy crap
    const dtToPush = {
      DealTranId: getId(),
      DealId,
      TranType: Type,
      CategoryId,
      ContactId,
      Price,
      Quantity,
      Description,
      DebitAccount,
      CreditAccount,
      Pending: true,
    };
    this.dealTransactions.push(dtToPush);
    return dtToPush;
  }

  @action addVoucherTransaction({
    VoucherId,
    Category,
    Price,
    Quantity,
    Description,
    Account,
  }) {
    // TODO: validate holy crap.
    const itToPush = {
      VoucherTranId: getId(),
      VoucherId,
      CategoryId: Category,
      Price,
      Quantity,
      Description,
      AccountId: Account,
      Pending: true,
    };
    this.voucherTransactions.push(itToPush);
    return itToPush;
  }

  @action loadDeals = async dealIds => {
    const response = await authStore.request.get(
      `${process.env.REACT_APP_API}/api/deal-map-deal/${dealIds.join(',')}`,
    );

    this.setDealMap(addPersistedToDealMap(response.data, 'deal'));
  };

  @action unloadDeal = async deal => {
    if (this.isAnythingPending) {
      return;
    }

    this.deals = this.deals.filter(d => d.DealId !== deal.DealId);
    const vouchersToUnload = this.getVouchersOnDeal(deal);
    this.vouchers = this.vouchers.filter(
      i => !vouchersToUnload.find(i2 => i2.VoucherId === i.VoucherId),
    );
    this.reset();
  };

  @action unloadVoucher = async voucher => {
    if (this.isAnythingPending) {
      return;
    }

    this.vouchers = this.vouchers.filter(
      i => i.VoucherId !== voucher.VoucherId,
    );
    const dealsToUnload = this.getDealsOnVoucher(voucher);
    this.deals = this.deals.filter(
      d => !dealsToUnload.find(d2 => d2.DealId === d.DealId),
    );
    this.reset();
  };

  @action removePendingDeal = async deal => {
    const removePendingDtvtsAssociatedWithDeal = () => {
      const dtList = this.getDealTransactionsOnDeal(deal);
      return this.dtvts.filter(
        i => !dtList.some(dt => dt.DealTranId === i.DealTranId),
      );
    };
    this.dtvts = removePendingDtvtsAssociatedWithDeal();
    this.dealTransactions = this.dealTransactions.filter(
      i => i.DealId !== deal.DealId,
    );
    this.deals = this.deals.filter(i => i.DealId !== deal.DealId);
  };

  @action removePendingVoucher = async voucher => {
    const removePendingDtvtsAssociatedWithVoucher = () => {
      const vtList = this.getVoucherTransactionsOnVoucher(voucher);
      return this.dtvts.filter(
        i => !vtList.some(vt => vt.VoucherTranId === i.VoucherTranId),
      );
    };
    this.dtvts = removePendingDtvtsAssociatedWithVoucher();
    this.voucherTransactions = this.voucherTransactions.filter(
      i => i.VoucherId !== voucher.VoucherId,
    );
    this.vouchers = this.vouchers.filter(
      i => i.VoucherId !== voucher.VoucherId,
    );
  };

  @action loadData = async ({ dealIds, voucherIds }) => {
    const promises = [];
    if (dealIds && dealIds.length > 0) {
      promises.push(this.loadDeals(dealIds));
    }

    if (voucherIds && voucherIds.length > 0) {
      promises.push(this.loadVouchers(voucherIds));
    }

    return Promise.all(promises);
  };

  @action reset = () => {
    const dealIds = this.deals.filter(d => d.Seed).map(d => d.DealId);
    const voucherIds = this.vouchers.filter(v => v.Seed).map(v => v.VoucherId);

    this.clearState();

    return this.loadData({ dealIds, voucherIds });
  };

  @action clearState = () => {
    this.deals = [];
    this.dealTransactions = [];
    this.dtvts = [];
    this.voucherTransactions = [];
    this.vouchers = [];
    this.selectedVoucherTransaction = null;
    this.selectedDealTransaction = null;
    this.dealMapperViewStore = new DealMapperViewStore(this);
  };

  @action updateDeal = deal => {
    const dealIndex = this.deals.findIndex(d => d.DealId === deal.DealId);
    this.deals[dealIndex] = {
      ...deal,
      Pending: true,
    };
  };

  @action createVoucher = ({
    VoucherTypeId,
    VoucherNumber,
    Description,
    VoucherDate,
    Amount,
    ContactId,
    DueDate,
    StatusId,
    AccountId,
  }) => {
    const newVoucher = {
      Pending: true,
      VoucherId: getId(),
      VoucherTypeId,
      VoucherNumber,
      Description,
      VoucherDate,
      Amount,
      ContactId,
      DueDate,
      StatusId,
      AccountId,
    };

    this.vouchers.push(newVoucher);
  };

  @action updateDeal = deal => {
    const dealIndex = this.deals.findIndex(d => d.DealId === deal.DealId);
    this.deals[dealIndex] = {
      ...deal,
      Pending: true,
    };
  };

  @action updateDealTransaction = dealTransaction => {
    const dealTransactionIndex = this.dealTransactions.findIndex(
      dt => dt.DealTranId === dealTransaction.DealTranId,
    );
    this.dealTransactions[dealTransactionIndex] = {
      ...dealTransaction,
      Pending: true,
    };
  };

  @action updateVoucher = voucher => {
    const VoucherIndex = this.vouchers.findIndex(
      i => i.VoucherId === voucher.VoucherId,
    );
    this.vouchers[VoucherIndex] = {
      ...voucher,
      Pending: true,
    };
  };

  @action updateVoucherTransaction = voucherTransaction => {
    const voucherTransactionIndex = this.voucherTransactions.findIndex(
      it => it.VoucherTranId === voucherTransaction.VoucherTranId,
    );

    this.voucherTransactions[voucherTransactionIndex] = {
      ...voucherTransaction,
      Pending: true,
    };
  };

  @action updateDtvt = dtvt => {
    const dtvtIndex = this.dtvts.findIndex(
      dt =>
        dt.VoucherId === dtvt.VoucherId && dt.DealTranId === dtvt.DealTranId,
    );
    this.dtvts[dtvtIndex] = {
      ...dtvt,
      Pending: true,
    };
  };

  @computed get exportableData() {
    return [
      this.deals.map(d => JSON.stringify(toJS(d))),
      this.dealTransactions.map(dt => JSON.stringify(toJS(dt))),
      this.dtvts.map(dtvt => JSON.stringify(toJS(dtvt))),
      this.dtvts.map(dtvt => JSON.stringify(toJS(dtvt))),
      this.voucherTransactions.map(it => JSON.stringify(toJS(it))),
      this.vouchers.map(i => JSON.stringify(toJS(i))),
    ];
  }

  updateObjectToPersisted = array =>
    array
      .filter(i => !i.PendingDelete)
      .map(i => ({
        ...i,
        Pending: undefined,
        Persisted: true,
      }));

  @action persistState = async () => {
    const dealMap = formatForPersist(this.dealMap);

    try {
      const { data } = await authStore.request.post(
        `${process.env.REACT_APP_API}/api/deal-map/`,
        {
          dealMap,
        },
      );

      const dealIdMappings = data[0];
      const dealTransactionIdMappings = data[1];
      const voucherTransactionIdMappings = data[2];
      const voucherIdMappings = data[3];

      // Trade out temp ids with the new ones
      this.deals = this.deals.map(d => {
        if (isIdTemp(d.DealId)) {
          const tempId = d.DealId.replace('tmp-', '');
          const newlyCreatedDeal = dealIdMappings.find(
            dIdM => dIdM.DealId === tempId,
          );
          const { DealId, DealNumber } = newlyCreatedDeal;
          return {
            ...d,
            DealId,
            DealNumber,
          };
        }

        return d;
      });

      this.dealTransactions = this.dealTransactions.map(dt => {
        const newDealTransaction = {
          ...dt,
        };

        if (isIdTemp(dt.DealTranId)) {
          const tempId = dt.DealTranId.replace('tmp-', '');
          const { DealTranId } = dealTransactionIdMappings.find(
            dtIdM => dtIdM.DealTranId === tempId,
          );
          newDealTransaction.DealTranId = DealTranId;
        }

        if (isIdTemp(dt.DealId)) {
          const tempId = dt.DealId.replace('tmp-', '');
          const { DealId } = dealIdMappings.find(
            dIdM => dIdM.DealId === tempId,
          );
          newDealTransaction.DealId = DealId;
        }

        return newDealTransaction;
      });

      this.dtvts = this.dtvts.map(dtvt => {
        const newdtvt = {
          ...dtvt,
        };

        if (isIdTemp(dtvt.DealTranId)) {
          const tempId = dtvt.DealTranId.replace('tmp-', '');
          const { DealTranId } = dealTransactionIdMappings.find(
            dtIdM => dtIdM.DealTranId === tempId,
          );
          newdtvt.DealTranId = DealTranId;
        }
        if (isIdTemp(dtvt.VoucherTranId)) {
          const tempId = dtvt.VoucherTranId.replace('tmp-', '');
          const { VoucherTranId } = voucherTransactionIdMappings.find(
            itIdM => itIdM.VoucherTranId === tempId,
          );
          newdtvt.VoucherTranId = VoucherTranId;
        }

        return newdtvt;
      });

      this.voucherTransactions = this.voucherTransactions.map(it => {
        const newVoucherTransaction = {
          ...it,
        };

        if (isIdTemp(it.VoucherTranId)) {
          const tempId = it.VoucherTranId.replace('tmp-', '');
          const { VoucherTranId } = voucherTransactionIdMappings.find(
            itIdM => itIdM.VoucherTranId === tempId,
          );
          newVoucherTransaction.VoucherTranId = VoucherTranId;
        }

        if (isIdTemp(it.VoucherId)) {
          const tempId = it.VoucherId.replace('tmp-', '');
          const { VoucherId } = voucherIdMappings.find(
            iIdM => iIdM.VoucherId === tempId,
          );
          newVoucherTransaction.VoucherId = VoucherId;
        }

        return newVoucherTransaction;
      });

      this.vouchers = this.vouchers.map(i => {
        if (isIdTemp(i.VoucherId)) {
          const tempId = i.VoucherId.replace('tmp-', '');
          const newlyCreatedVoucher = voucherIdMappings.find(
            iIdM => iIdM.VoucherId === tempId,
          );
          const { VoucherId, VoucherNumber } = newlyCreatedVoucher;

          return {
            ...i,
            VoucherId,
            VoucherNumber,
          };
        }

        return i;
      });

      this.deals = this.updateObjectToPersisted(this.deals);
      this.dealTransactions = this.updateObjectToPersisted(
        this.dealTransactions,
      );
      this.dtvts = this.updateObjectToPersisted(this.dtvts);
      this.voucherTransactions = this.updateObjectToPersisted(
        this.voucherTransactions,
      );
      this.vouchers = this.updateObjectToPersisted(this.vouchers);
    } catch (e) {
      console.error(e);
      // TODO: dump to textfile so we can recover it
    }
  };

  getDtvtByDealTransactionAndVoucherTransaction = (
    dealTransaction,
    voucherTransaction,
  ) =>
    this.dtvts.find(
      dtvt =>
        dtvt.DealTranId === dealTransaction.DealTranId &&
        dtvt.VoucherTranId === voucherTransaction.VoucherTranId,
    );

  getAmountToVoucher = dealTransaction => {
    const dtvtsOnDealTransaction = this.getDtvtsOnDealTransaction(
      dealTransaction,
    );
    const dtvtSum = dtvtsOnDealTransaction.reduce((agg, dtvt) => {
      return agg + parseFloat(dtvt.Amount);
    }, 0);

    // FIXME: reevaluate

    // return dealTransaction.Price - dtvtSum;

    // getAmountToVoucher returns DT total minus current DTVT sum
    return (
      multiplyTwo(dealTransaction.Price, dealTransaction.Quantity) - dtvtSum
    );
  };

  getMappableVouchers = () => this.vouchers.filter(d => d.StatusId < 5);

  doesVoucherMatchDealTransaction = (dealTransaction, voucher) => {
    // if contacts are different
    if (voucher.ContactId !== dealTransaction.ContactId) return false;

    // If voucher type is different
    if (
      voucher.VoucherTypeId !==
      getVoucherTypeFromDealTransaction(dealTransaction, this.isInvoiceDefault)
    )
      return false;

    // if accountId is different
    if (
      voucher.AccountId !==
      getVoucherAccountFromDealTransaction(dealTransaction)
    )
      return false;

    return true;
  };

  doesVoucherTransactionMatchDealTransaction = (
    dealTransaction,
    voucherTransaction,
  ) => {
    if (
      voucherTransaction.AccountId !==
      getVoucherTransactionAccountFromDealTransaction(dealTransaction)
    )
      return false;

    // if the categories don't match
    if (voucherTransaction.CategoryId !== dealTransaction.CategoryId)
      return false;

    return true;
  };

  isVoucherEditable = voucher => voucher.StatusId < 4;

  isVoucherMappable = voucher => voucher.StatusId < 5;

  getEditableVoucherTransactionsOnDealTransaction = dealTransaction => {
    const dtvtsOnDealTransaction = this.getDtvtsOnDealTransaction(
      dealTransaction,
    );
    const voucherTransactionOnDealTransaction = dtvtsOnDealTransaction.reduce(
      (agg, dtvt) => {
        const vt = this.getVoucherTransactionOnDtvt(dtvt);
        if (vt) {
          return [...agg, vt];
        }
        return agg;
      },
      [],
    );
    return voucherTransactionOnDealTransaction.filter(vt => {
      const voucher = this.getVoucherOnVoucherTransaction(vt);
      const isVoucherCorrectType = this.isInvoiceDefault
        ? voucher.VoucherTypeId <= 4
        : voucher.VoucherTypeId > 4;
      return (
        this.doesVoucherMatchDealTransaction(dealTransaction, voucher) &&
        this.isVoucherEditable(voucher) &&
        isVoucherCorrectType
      );
    });
  };

  getEditableMatchingVouchers = dealTransaction =>
    this.vouchers.filter(voucher => {
      // checks if voucher is correct type invoices
      if (this.isInvoiceDefault && voucher.VoucherTypeId > 4) return false;

      // checks if voucher is correct type direct payment
      if (!this.isInvoiceDefault && voucher.VoucherTypeId <= 4) return false;

      if (!this.isVoucherEditable(voucher)) return false;

      if (voucher.ContactId !== dealTransaction.ContactId) return false;

      if (
        voucher.AccountId !==
        getVoucherAccountFromDealTransaction(dealTransaction)
      )
        return false;

      if (
        voucher.VoucherTypeId !==
        getVoucherTypeFromDealTransaction(
          dealTransaction,
          this.isInvoiceDefault,
        )
      )
        return false;

      return true;
    });

  getMappableMatchingVouchers = dealTransaction =>
    this.vouchers.filter(voucher => {
      // checks if voucher is correct type invoices
      if (this.isInvoiceDefault && voucher.VoucherTypeId > 4) return false;
      // checks if voucher is correct type direct payment
      if (!this.isInvoiceDefault && voucher.VoucherTypeId <= 4) return false;
      if (!this.isVoucherMappable(voucher)) return false;
      if (voucher.ContactId !== dealTransaction.ContactId) return false;
      if (
        voucher.AccountId !==
        getVoucherAccountFromDealTransaction(dealTransaction)
      )
        return false;

      if (
        voucher.VoucherTypeId !==
        getVoucherTypeFromDealTransaction(
          dealTransaction,
          this.isInvoiceDefault,
        )
      )
        return false;

      return true;
    });

  getMatchingVoucherTransactionOnDealTransaction = ({
    dealTransaction,
    voucherTransactions,
  }) => {
    const voucherTransactionsToLoopThrough =
      voucherTransactions || this.voucherTransactions;

    return voucherTransactionsToLoopThrough.filter(it =>
      this.doesVoucherTransactionMatchDealTransaction(dealTransaction, it),
    );
  };

  getVoucherTransactionsWithoutHangingDtvts = ({
    dealTransaction,
    voucherTransactions,
  }) => {
    const voucherTransactionsToLoopThrough =
      voucherTransactions || this.voucherTransactions;
    return voucherTransactionsToLoopThrough.filter(it => {
      const dtvts = this.getDtvtsOnVoucherTransaction(it);
      // voucher transactions need 1 or fewer dtvts to not have hanging dtvts
      if (!dealTransaction && dtvts.length > 0) {
        return false;
      }

      if (dealTransaction && dtvts.length > 1) {
        return false;
      }

      if (
        dealTransaction &&
        dtvts.length === 1 &&
        !this.getDtvtByDealTransactionAndVoucherTransaction(dealTransaction, it)
      ) {
        return false;
      }

      return true;
    });
  };

  @action voucherDeals = () => {
    this.isInvoiceDefault = orgStore.settings.VoucherTypeDefault === 'IN';

    this.deals.forEach(deal => {
      this.getDealTransactionsOnDeal(deal).forEach(dt => {
        const amountToVoucher = this.getAmountToVoucher(dt);
        if (amountToVoucher === 0) {
          return;
        }

        const editableVoucherTransactions = this.getEditableVoucherTransactionsOnDealTransaction(
          dt,
        );

        // if there are 1 or more editable voucher transactions
        if (editableVoucherTransactions.length > 0) {
          const matchingEditableVoucherTransactions = this.getMatchingVoucherTransactionOnDealTransaction(
            {
              dealTransaction: dt,
              voucherTransactions: editableVoucherTransactions,
            },
          );

          // if there are 1 or more editable voucher transactions that match
          if (matchingEditableVoucherTransactions.length > 0) {
            // Update voucher transaction amount and dtvt amount with original + unVoucherd amount
            const itToEdit = matchingEditableVoucherTransactions[0];
            const dtvtToEdit = this.getDtvtByDealTransactionAndVoucherTransaction(
              dt,
              itToEdit,
            );
            console.log('DTVT 2');

            itToEdit.Pending = true;

            itToEdit.Price = divideTwo(
              multiplyTwo(itToEdit.Price, itToEdit.Quantity) + amountToVoucher,
              dt.Quantity,
            );
            itToEdit.Quantity = dt.Quantity;
            dtvtToEdit.Amount =
              Number(dtvtToEdit.Amount) + Number(amountToVoucher);
            dtvtToEdit.Pending = true;
            // terminate
          } else {
            // get all editable vouchers without hanging chads
            const editableVoucherTransactionsWithoutHangingDtvts = this.getVoucherTransactionsWithoutHangingDtvts(
              {
                dealTransaction: dt,
                voucherTransactions: editableVoucherTransactions,
              },
            );

            // If there are 1 or more editable voucher transactions without hanging chads
            if (editableVoucherTransactionsWithoutHangingDtvts.length > 0) {
              // Update voucher transaction with CATEGORY, TYPE, AMOUNT = original +
              //  unVoucherdAmount and dtvt amount same
              const itToEdit =
                editableVoucherTransactionsWithoutHangingDtvts[0];
              const dtvtToEdit = this.getDtvtByDealTransactionAndVoucherTransaction(
                dt,
                itToEdit,
              );

              itToEdit.Price = divideTwo(
                multiplyTwo(itToEdit.Price, itToEdit.Quantity) +
                  amountToVoucher,
                dt.Quantity,
              );
              itToEdit.Quantity = dt.Quantity;
              itToEdit.AccountId = getVoucherTransactionAccountFromDealTransaction(
                dt,
              );
              itToEdit.Pending = true;
              itToEdit.CategoryId = dt.CategoryId;
              console.log('DTVT 3');

              dtvtToEdit.Amount =
                Number(dtvtToEdit.Amount) + Number(amountToVoucher);
              dtvtToEdit.Pending = true;
            } else {
              // take first voucher transaction's voucher and add new voucher transaction
              //  with unVoucherd amount and dtvt with unVoucherd amount
              const voucherToAddTo = editableVoucherTransactions[0];
              const newIt = this.addVoucherTransaction({
                VoucherId: voucherToAddTo.VoucherId,
                Category: dt.CategoryId,
                Price: amountToVoucher,
                Quantity: 1,
                Description: assignByPriority([
                  dt.Description,
                  deal.Description,
                  deal.DealNumber,
                ]),
                Account: getVoucherTransactionAccountFromDealTransaction(dt),
              });
              console.log('DTVT 4');
              this.dtvts.push({
                DealTranId: dt.DealTranId,
                VoucherTranId: newIt.VoucherTranId,
                // Amount: amountToVoucher,
                Amount: multiplyTwo(amountToVoucher, 1),
                Pending: true,
              });
              // terminate
            }
          }
        } else {
          // get all mappable vouchers that match
          const mappableVouchers = this.getMappableMatchingVouchers(dt);
          //

          // if there are 1 or more mappable vouchers that match
          if (mappableVouchers.length > 0) {
            // Get all voucher transactions on mappable vouchers that have the same TYPE,
            //  ACCOUNT,  as deal transaction and NO HANGING IT CHADS
            const voucherTransactionsOnVouchers = mappableVouchers.reduce(
              (agg, voucher) => {
                return [
                  ...agg,
                  ...this.getVoucherTransactionsOnVoucher(voucher),
                ];
              },
              [],
            );

            const matchingVoucherTransactions = this.getMatchingVoucherTransactionOnDealTransaction(
              {
                dealTransaction: dt,
                voucherTransactions: voucherTransactionsOnVouchers,
              },
            );

            // If there are 1 or more matching voucher transactions
            if (matchingVoucherTransactions.length > 0) {
              // Get all matching voucher transactions that have the same AMOUNT
              const matchingVoucherTransactionsSameAmount = matchingVoucherTransactions.filter(
                mit => mit.Price === amountToVoucher,
              );

              // If there are 1 or more matching voucher transactions with the same amount
              if (matchingVoucherTransactionsSameAmount.length > 0) {
                const itToMapTo = matchingVoucherTransactionsSameAmount[0];
                // Create dtvt mapping for unVoucherd amount to voucherTransaction
                console.log('DTVT 5');
                this.dtvts.push({
                  DealTranId: dt.DealTranId,
                  VoucherTranId: itToMapTo.VoucherTranId,
                  // Amount: dt.Price,
                  Amount: multiplyTwo(dt.Price, dt.Quantity),
                  Pending: true,
                });
                const allDtvts = this.getDtvtsOnVoucher(itToMapTo);

                const newItTotal = allDtvts.reduce(
                  (a, b) => a + (parseFloat(b.Amount) || 0),
                  0,
                );

                itToMapTo.Price = newItTotal;
                itToMapTo.Quantity = dt.Quantity;
                itToMapTo.Pending = true;

                // terminate
              } else {
                // Take first matching voucher transaction and update it's amount to
                //  dt.amount and map to it with dtvt amount being the dt.amount
                const itToUpdate = matchingVoucherTransactions[0];

                console.log('DTVT 6');
                this.dtvts.push({
                  DealTranId: dt.DealTranId,
                  VoucherTranId: itToUpdate.VoucherTranId,
                  // Amount: dt.Price,
                  Amount: multiplyTwo(dt.Price, dt.Quantity),
                  Pending: true,
                });

                const allDtvts = this.getDtvtsOnVoucher(itToUpdate);

                const newItTotal = allDtvts.reduce(
                  (a, b) => a + (parseFloat(b.Amount) || 0),
                  0,
                );

                itToUpdate.Price = newItTotal;
                itToUpdate.Quantity = dt.Quantity;
                itToUpdate.Pending = true;

                // terminate
              }
            } else {
              // Get all mappable voucher transactions without hanging chads
              const editableVoucherTransactionsWithoutHangingDtvts = mappableVouchers.reduce(
                (agg, v) => {
                  const vtsOnVoucher = this.getVoucherTransactionsOnVoucher(v);
                  // filter out all voucher transactions with dtvts
                  const vtsWithoutHangingDtvts = vtsOnVoucher.filter(vt => {
                    const dtvtsOnVt = this.getDtvtsOnVoucherTransaction(vt);
                    return dtvtsOnVt.length === 0;
                  });

                  return [...agg, ...vtsWithoutHangingDtvts];
                },
                [],
              );
              // Are there 1 or more mappable voucher transactions without hanging chads?
              if (editableVoucherTransactionsWithoutHangingDtvts.length > 0) {
                // Update voucher transaction with ACCOUNT, TYPE, AMOUNT = original +
                // uninvoicedAmount and dtit amount same
                const vtToUpdate =
                  editableVoucherTransactionsWithoutHangingDtvts[0];
                vtToUpdate.Price = dt.Price;
                vtToUpdate.Quantity = dt.Quantity;
                vtToUpdate.Pending = true;
                vtToUpdate.AccountId = getVoucherTransactionAccountFromDealTransaction(
                  dt,
                );

                console.log('DTVT 7');
                this.dtvts.push({
                  DealTranId: dt.DealTranId,
                  VoucherTranId: vtToUpdate.VoucherTranId,
                  // Amount: dt.Price,
                  Amount: multiplyTwo(dt.Price * dt.Quantity),
                  Pending: true,
                });
              } else {
                // Take the first mappable voucher and create an voucher transaction on it
                //  with amount = dt.amount  and then map to it with dtvt amount being dt.amount
                const VoucherToAddTo = mappableVouchers[0];

                const newIt = this.addVoucherTransaction({
                  VoucherId: VoucherToAddTo.VoucherId,
                  Category: dt.CategoryId,
                  Price: dt.Price,
                  Quantity: dt.Quantity,
                  Description: assignByPriority([
                    dt.Description,
                    deal.Description,
                    deal.DealNumber,
                  ]),
                  Account: getVoucherTransactionAccountFromDealTransaction(dt),
                });

                console.log('DTVT 8');
                this.dtvts.push({
                  DealTranId: dt.DealTranId,
                  VoucherTranId: newIt.VoucherTranId,
                  // Amount: dt.Price,
                  Amount: multiplyTwo(dt.Price, dt.Quantity),
                  Pending: true,
                });

                // terminate
              }
            }
          } else {
            const vList = this.getVouchersOnDeal(deal);
            const uniqueVList = vList.filter(
              (v, i, a) => a.findIndex(t => t.VoucherId === v.VoucherId) === i,
            );
            // Create new voucher and add voucher transaction to it with amount = dt.amount
            //  and then map to it with dtvt amount being dt.amount
            const VoucherTypeId = getVoucherTypeFromDealTransaction(
              dt,
              this.isInvoiceDefault,
            );
            const newVoucher = {
              Pending: true,
              VoucherId: getId(),
              DueDate:
                VoucherTypeId && VoucherTypeId < 5
                  ? deal.ContractDate
                  : format(new Date(), 'YYYY-MM-DD'),
              ContactId: dt.ContactId,
              VoucherNumber: `${deal.Title}-${uniqueVList.length + 1}`,
              VoucherTypeId,
              AccountId: getVoucherAccountFromDealTransaction(dt),
              StatusId: 1,
              IsEditable: true,
              VoucherDate: format(new Date(), 'YYYY-MM-DD'),
              Description: assignByPriority([
                deal.Title,
                deal.Description,
                deal.DealNumber,
              ]),
            };

            this.vouchers.push(newVoucher);
            const newIt = this.addVoucherTransaction({
              VoucherId: newVoucher.VoucherId,
              Category: dt.CategoryId,
              Price: divideTwo(amountToVoucher, dt.Quantity),
              Quantity: dt.Quantity,
              Description: assignByPriority([
                dt.Description,
                deal.Description,
                deal.DealNumber,
              ]),
              Account: getVoucherTransactionAccountFromDealTransaction(dt),
            });
            console.log('DTVT 9');
            this.dtvts.push({
              DealTranId: dt.DealTranId,
              VoucherTranId: newIt.VoucherTranId,
              Amount: multiplyTwo(amountToVoucher, dt.Quantity),
              // Amount: amountToVoucher,
              Pending: true,
            });

            // terminate
          }
        }
      });
    });
  };

  getUnmappedAmount = voucherTransaction => {
    const dtvtsOnVoucherTransaction = this.getDtvtsOnVoucherTransaction(
      voucherTransaction,
    );
    const dtvtSum = dtvtsOnVoucherTransaction.reduce((agg, dtvt) => {
      return agg + parseFloat(dtvt.Amount);
    }, 0);

    // FIXME: reevaluate
    // return voucherTransaction.Price - dtvtSum;

    return (
      multiplyTwo(voucherTransaction.Price, voucherTransaction.Quantity) -
      dtvtSum
    );
  };

  getEditableDealTransactionsOnVoucherTransaction = voucherTransaction => {
    const dtvtsOnIt = this.getDtvtsOnVoucherTransaction(voucherTransaction);
    const dealTransactionsOnVoucherTransactions = dtvtsOnIt.reduce(
      (agg, dtvt) => {
        return [...agg, this.getDealTransactionOnDtvt(dtvt)];
      },
      [],
    );
    return dealTransactionsOnVoucherTransactions.filter(dtOnIt => {
      const deal = this.getDealOnDealTransaction(dtOnIt);
      if (deal.StatusId < 4) return true;
      return false;
    });
  };

  getEditableDeals = () => this.deals.filter(d => d.StatusId < 4);

  getMatchingDealTransactionsFromDeals = (
    voucher,
    voucherTransaction,
    deals,
  ) => {
    const dealTransactions = deals.reduce((agg, deal) => {
      return [...agg, ...this.getDealTransactionsOnDeal(deal)];
    }, []);

    return dealTransactions.filter(dt => {
      if (getVoucherAccountFromDealTransaction(dt) !== voucher.AccountId)
        return false;

      if (
        getVoucherTransactionAccountFromDealTransaction(dt) !==
        voucherTransaction.AccountId
      )
        return false;

      if (dt.CategoryId !== voucherTransaction.CategoryId) return false;

      if (
        getVoucherTypeFromDealTransaction(dt, this.isInvoiceDefault) !==
        voucher.VoucherTypeId
      )
        return false;

      if (dt.ContactId !== voucher.ContactId) return false;

      if (voucherTransaction.Quantity !== dt.Quantity) return false;

      return true;
    });
  };

  doesDealTransactionMatchVoucherTransaction = (
    voucherTransaction,
    dealTransaction,
  ) => {
    const voucher = this.getVoucherOnVoucherTransaction(voucherTransaction);
    if (
      getVoucherAccountFromDealTransaction(dealTransaction) !==
      voucher.AccountId
    )
      return false;

    if (
      getVoucherTransactionAccountFromDealTransaction(dealTransaction) !==
      voucherTransaction.AccountId
    )
      return false;

    if (dealTransaction.CategoryId !== voucherTransaction.CategoryId)
      return false;

    if (
      getVoucherTypeFromDealTransaction(
        dealTransaction,
        this.isInvoiceDefault,
      ) !== voucher.VoucherTypeId
    )
      return false;

    if (dealTransaction.ContactId !== voucher.ContactId) return false;

    return true;
  };

  @action mapVouchers = () => {
    this.isInvoiceDefault = orgStore.settings.VoucherTypeDefault === 'IN';
    const mappableVouchers = this.getMappableVouchers();
    mappableVouchers.forEach(v => {
      this.getVoucherTransactionsOnVoucher(v).forEach(vt => {
        const unmappedAmount = this.getUnmappedAmount(vt);

        // is amount to be mapped 0
        if (unmappedAmount === 0) {
          return;
        }

        // Get all editable deal transactions mapped to the voucher transaction
        const editableDealTransactions = this.getEditableDealTransactionsOnVoucherTransaction(
          vt,
        );

        // Are there 1 or more editable deal transactions on the voucher transaction?
        if (editableDealTransactions.length > 0) {
          // Get all editable deal transactions on the IT  that match
          const matchingEditableDealTransactions = editableDealTransactions.filter(
            dt => this.doesDealTransactionMatchVoucherTransaction(vt, dt),
          );

          // Are there 1 or more editable deal transactions that match?
          if (matchingEditableDealTransactions.length > 0) {
            // Update first deal transaction's amount and dtvt.amount with original +
            //  unmapped amount
            const dtToUpdate = matchingEditableDealTransactions[0];
            const dtvtToUpdate = this.getDtvtByDealTransactionAndVoucherTransaction(
              dtToUpdate,
              vt,
            );
            dtToUpdate.Price = divideTwo(
              multiplyTwo(dtToUpdate.Price, dtToUpdate.Quantity) +
                Number(unmappedAmount),
              vt.Quantity,
            );

            dtToUpdate.Quantity = vt.Quantity;
            dtToUpdate.Pending = true;

            dtvtToUpdate.Amount =
              Number(dtvtToUpdate.Amount) + Number(unmappedAmount);
            dtvtToUpdate.Pending = true;
            // terminate
          } else {
            // Get all editable deal transactions without hanging chads
            const editableDealTransactionsWithoutHangingChads = matchingEditableDealTransactions.filter(
              mDt => {
                const dtvts = this.getDtvtsOnDealTransaction(mDt);
                // if there's more than one dtvt vt has a hanging chad
                if (dtvts.length > 1) return false;

                // If there's only one mapping on the deal transaction
                if (dtvts.length === 1) {
                  // if that mapping goes to our voucher transaction we good
                  return this.getDtvtByDealTransactionAndVoucherTransaction(
                    mDt,
                    vt,
                  );
                }

                return true;
              },
            );

            // Are there 1 or more editable deal transactions without hanging chads?
            if (editableDealTransactionsWithoutHangingChads.length > 0) {
              // Update deal transactions with ACCOUNT, TYPE, AMOUNT = original +unmapped
              //  amount/ CO-OPT DEAL TRANSACTION
              const dtToUpdate = editableDealTransactionsWithoutHangingChads[0];
              const dtvtToUpdate = this.getDtvtByDealTransactionAndVoucherTransaction(
                dtToUpdate,
                vt,
              );

              const {
                CreditAccount,
                DebitAccount,
              } = getDealTransactionAccounts(v, vt);

              dtToUpdate.TranType = getDealTransactionTypeFromVoucher(v);
              dtToUpdate.CreditAccount = CreditAccount;
              dtToUpdate.DebitAccount = DebitAccount;
              // dtToEdit.Price = divideTwo(
              //   multiplyTwo(dtToEdit.Price, dtToEdit.Quantity) + unmappedAmount,
              //   dt.Quantity,
              // );
              dtToUpdate.Price =
                Number(dtToUpdate.Price) + Number(unmappedAmount);
              dtToUpdate.Quantity = vt.Quantity;
              dtToUpdate.Pending = true;

              dtvtToUpdate.Amount =
                Number(dtvtToUpdate.Amount) + Number(unmappedAmount);
              dtvtToUpdate.Pending = true;
              // terminate
            } else {
              // Take first deal transaction's deal and add new deal transaction with
              //  unmapped amount + dtvt
              const dealToAddTo = this.getDealOnDealTransaction(
                editableDealTransactions[0],
              );

              const {
                CreditAccount,
                DebitAccount,
              } = getDealTransactionAccounts(v, vt);

              console.log('DTVT 10');

              const newDealTransaction = this.addDealTransaction({
                DealId: dealToAddTo.DealId,
                Type: getDealTransactionTypeFromVoucher(v),
                CategoryId: vt.CategoryId,
                ContactId: v.ContactId,
                Price: divideTwo(unmappedAmount, vt.Quantity),
                Quantity: vt.Quantity,
                Description: vt.Description,
                CreditAccount,
                DebitAccount,
              });

              this.dtvts.push({
                DealTranId: newDealTransaction.DealTranId,
                VoucherTranId: vt.VoucherTranId,
                Amount: unmappedAmount,
                // Amount: multiplyTwo(unmappedAmount, vt.Quantity),
                Pending: true,
              });
              // terminate
            }
          }
        } else {
          // Get all editiable and matching deals
          const editableDeals = this.getEditableDeals();

          // if there are 1 or more editable deals
          if (editableDeals.length > 0) {
            //  Get all matching deal transactions WITHOUT HANGING CHADS.
            const matchingDealTransactions = this.getMatchingDealTransactionsFromDeals(
              v,
              vt,
              editableDeals,
            );
            // if there are 1 or more matching deal transactions
            if (matchingDealTransactions.length > 0) {
              // Get all matching deal transactions with SAME AMOUNT AS UNMAPPED AMOUNT
              const matchingDealTransactionsWithSameAmount = matchingDealTransactions.filter(
                mdt => mdt.Price === unmappedAmount,
              );

              // Are there 1 or more matching deal transactions with same amount?
              if (matchingDealTransactionsWithSameAmount.length > 0) {
                // Create dtvt mapping for unnapped amount
                const dtToMapTo = matchingDealTransactionsWithSameAmount[0];

                console.log('DTVT 11');
                this.dtvts.push({
                  DealTranId: dtToMapTo.DealTranId,
                  VoucherTranId: vt.VoucherTranId,
                  Amount: unmappedAmount,
                  Pending: true,
                });
                const allDtvts = this.getDtvtsOnDealTransaction(dtToMapTo);

                const newDtTotal = allDtvts.reduce(
                  (a, b) => a + (parseFloat(b.Amount) || 0),
                  0,
                );

                dtToMapTo.Price = newDtTotal;
                // dtToMapTo.Price = divideTwo(
                //   multiplyTwo(dtToMapTo.Price, dtToMapTo.Quantity) + newDtTotal,
                //   vt.Quantity,
                // );
                dtToMapTo.Quantity = vt.Quantity;
                dtToMapTo.Pending = true;
                // terminate
              } else {
                // Take first matching deal transaction from previous array and update vt's
                //  amount + create dtvt
                const dtToUpdate = matchingDealTransactions[0];

                console.log('DTVT 12');

                this.dtvts.push({
                  DealTranId: dtToUpdate.DealTranId,
                  VoucherTranId: vt.VoucherTranId,
                  Amount: unmappedAmount,
                  Pending: true,
                });

                const allDtvts = this.getDtvtsOnDealTransaction(dtToUpdate);

                const newDtTotal = allDtvts.reduce(
                  (a, b) => a + (parseFloat(b.Amount) || 0),
                  0,
                );

                console.log(newDtTotal);

                dtToUpdate.Price = divideTwo(newDtTotal, vt.Quantity);
                dtToUpdate.Quantity = vt.Quantity;
                dtToUpdate.Pending = true;

                // terminate
              }
            } else {
              // get all editable deal transactions without hanging chads
              const dtsWithoutHangingDtvts = editableDeals.reduce(
                (agg, deal) => {
                  const dtsOnDealWithoutHangingDtvts = this.getDealTransactionsOnDeal(
                    deal,
                  ).filter(
                    dt => this.getDtvtsOnDealTransaction(dt).length === 0,
                  );

                  return [...agg, ...dtsOnDealWithoutHangingDtvts];
                },
                [],
              );
              // Are there 1 or more editable deal transactions without hanging chads?
              if (dtsWithoutHangingDtvts.length > 0) {
                // Update deal transactions with ACCOUNT, TYPE, AMOUNT = original +unmapped amount/ CO-OPT DEAL TRANSACTION
                const dtToUpdate = dtsWithoutHangingDtvts[0];

                dtToUpdate.Price = vt.Price;
                dtToUpdate.Quantity = vt.Quantity;
                const {
                  creditAccount,
                  debitAccount,
                } = getDealTransactionAccounts(v, vt);
                dtToUpdate.DebitAccount = debitAccount;
                dtToUpdate.CreditAccount = creditAccount;
                dtToUpdate.TranType = getDealTransactionTypeFromVoucher(v);
                dtToUpdate.Pending = true;

                console.log('DTVT 13');

                this.dtvts.push({
                  DealTranId: dtToUpdate.DealTranId,
                  VoucherTranId: vt.VoucherTranId,
                  Amount: unmappedAmount,
                  Pending: true,
                });
              } else {
                // Take first editable matching deals and create new deal transactions on with
                //  with amount = unmapped amount and dtvt with matching amount
                const dealToAddTo = editableDeals[0];
                const {
                  CreditAccount,
                  DebitAccount,
                } = getDealTransactionAccounts(v, vt);

                const newDealTransaction = this.addDealTransaction({
                  DealId: dealToAddTo.DealId,
                  Type: getDealTransactionTypeFromVoucher(v),
                  CategoryId: vt.CategoryId,
                  ContactId: v.ContactId,
                  Price: divideTwo(unmappedAmount, vt.Quantity),
                  Quantity: vt.Quantity,
                  Description: vt.Description,
                  DebitAccount,
                  CreditAccount,
                });
                console.log('DTVT 14');

                this.dtvts.push({
                  DealTranId: newDealTransaction.DealTranId,
                  VoucherTranId: vt.VoucherTranId,
                  Amount: unmappedAmount,
                  Pending: true,
                });

                // terminate
              }
            }
          } else {
            // Create new deal and deal transaction with dt.Amount = amount to be mapped and
            //  dtvt.Amount = amount to be mapped
            const newDeal = {
              DealId: getId(),
              Title: assignByPriority([
                v.Description,
                v.ContactName,
                'Untitled Deal',
              ]),
              Pending: true,
              ContractDate: assignByPriority([
                v.VoucherDate,
                format(new Date(), 'YYYY-MM-DD'),
              ]),
              TransactionDate: assignByPriority([
                v.DueDate,
                format(new Date(), 'YYYY-MM-DD'),
              ]),
              BillableDate: assignByPriority([
                v.VoucherDate,
                format(new Date(), 'YYYY-MM-DD'),
              ]),
              PayableDate: assignByPriority([
                v.VoucherDate,
                format(new Date(), 'YYYY-MM-DD'),
              ]),
              StatusId: 0,
              DealNumber: 'AUTO-GENERATE',
            };

            this.deals.push(newDeal);
            const { CreditAccount, DebitAccount } = getDealTransactionAccounts(
              v,
              vt,
            );

            const newDt = this.addDealTransaction({
              DealId: newDeal.DealId,
              Type: getDealTransactionTypeFromVoucher(v),
              CategoryId: vt.CategoryId,
              ContactId: v.ContactId,
              Price: divideTwo(unmappedAmount, vt.Quantity),
              Quantity: vt.Quantity,
              Description: vt.Description,
              DebitAccount,
              CreditAccount,
            });

            console.log('DTVT 15');

            this.dtvts.push({
              DealTranId: newDt.DealTranId,
              VoucherTranId: vt.VoucherTranId,
              Amount: unmappedAmount,
              Pending: true,
            });
            // terminate
          }
        }
      });
    });
  };
}
